import { UserAvatar } from "@/components/ui/stacked-avatars";
import { useNotificationMuteActivity } from "@/lib/hooks/conversations/use-notification-mute-activity";
import { useAudioPlayback } from "@/lib/hooks/use-audio-playback";
import {
  dimUnplayedBars,
  formatAudioDuration,
  useAudioBlob,
  useStaticAudioWaveform,
} from "@/lib/hooks/use-audio-waveform";
import { cn } from "@/lib/utils";
import { colorsV2 } from "@somethings-ui/core";
import { Pause, Play } from "lucide-react";

type Variant = "dark" | "light";

interface AudioWaveformPlayerProps {
  audioUrl: string;
  variant?: Variant;
  speaker?: string;
  avatarUrl?: string;
  className?: string;
}

interface VariantTheme {
  playButtonBg: string;
  playButtonIcon: string;
  barColor: string;
  durationText: string;
  placeholderBg: string;
}

const THEMES: Record<Variant, VariantTheme> = {
  dark: {
    playButtonBg: colorsV2.brand.tangelo,
    playButtonIcon: "text-white",
    barColor: colorsV2.brand.tangelo,
    durationText: "text-white",
    placeholderBg: "bg-white/15",
  },
  light: {
    playButtonBg: colorsV2.brand.tangelo,
    playButtonIcon: "text-white",
    barColor: colorsV2.brand.tangelo,
    durationText: "text-secondary",
    placeholderBg: "bg-black/10",
  },
};

export function AudioWaveformPlayer({
  audioUrl,
  variant = "dark",
  speaker,
  avatarUrl,
  className,
}: AudioWaveformPlayerProps) {
  const theme = THEMES[variant];
  const blob = useAudioBlob(audioUrl);
  const { isPlaying, progressMs, durationMs, toggle } =
    useAudioPlayback(audioUrl);

  useNotificationMuteActivity(isPlaying, "deep-thoughts-playback");

  const bars = useStaticAudioWaveform(blob);
  const playedBars = dimUnplayedBars(bars, durationMs, progressMs);
  const showAvatar = !!speaker;

  return (
    <div className={cn("flex flex-col gap-4", className)}>
      {showAvatar ? (
        <div className="relative ml-1 h-[50px] w-[50px]">
          <UserAvatar
            imageUrl={avatarUrl}
            name={speaker ?? ""}
            size={50}
            showBorder
          />
        </div>
      ) : null}
      <div className="flex items-center gap-3 px-3">
        <button
          type="button"
          onClick={toggle}
          aria-label={isPlaying ? "Pause audio" : "Play audio"}
          className={cn(
            "flex h-8 w-8 shrink-0 items-center justify-center rounded-full",
            theme.playButtonIcon,
          )}
          style={{ background: theme.playButtonBg }}
        >
          {isPlaying ? (
            <Pause className="h-3.5 w-3.5" />
          ) : (
            <Play className="ml-0.5 h-3.5 w-3.5" />
          )}
        </button>
        {playedBars ? (
          <div
            className="flex flex-1 items-center gap-[1.5px]"
            style={{ height: 33 }}
          >
            {playedBars.map((v, i) => (
              <div
                key={i}
                style={{
                  width: 3,
                  height: `${Math.max(6, v * 33)}px`,
                  background: theme.barColor,
                  borderRadius: 110,
                }}
              />
            ))}
          </div>
        ) : (
          <div
            className={cn("h-8 flex-1 animate-pulse rounded", theme.placeholderBg)}
          />
        )}
        <span className={cn("text-sm font-normal", theme.durationText)}>
          {durationMs > 0 ? formatAudioDuration(durationMs) : "—"}
        </span>
      </div>
    </div>
  );
}
